import {
    aiErrorMessage,
    approveAiRunItems,
    createInventoryImport,
    discardAiRunItems,
    getAiRun,
    IAiRun,
    isAiRunFinished,
} from "@rpforms/shared/build/actions/ai";
import { PrimaryButton } from "@rpforms/shared/build/components/layout/Button";
import { push } from "connected-react-router";
import React, { useEffect, useRef, useState } from "react";
import { Alert, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

interface IModalInventoryImport {
    show: boolean;
    onClose: () => void;
    onFinished?: () => void;
}

export const IMPORT_INVENTORY_TYPE = "disposal";
export const ALLOWED_IMPORT_TYPES = ["application/pdf", "image/png", "image/jpeg"];
export const MAX_IMPORT_SIZE = 20 * 1024 * 1024;
export const POLL_INTERVAL_MS = 2000;
const MAX_SKIPPED_SHOWN = 10;

export const importFileError = (file: File | null): string | null => {
    if (!file) {
        return "Bitte eine Datei auswählen";
    }
    if (!ALLOWED_IMPORT_TYPES.includes(file.type)) {
        return "Nur PDF, JPG oder PNG";
    }
    if (file.size > MAX_IMPORT_SIZE) {
        return "Datei zu groß (max. 20 MB)";
    }
    return null;
};

export const progressLabel = (run: IAiRun): string => {
    const total = run.output?.pages_total || 0;
    const done = run.output?.pages_done || 0;
    if (run.status === "queued" || total === 0) {
        return "Datei wird vorbereitet…";
    }
    return `Seite ${Math.min(done + 1, total)} von ${total}`;
};

export const resultSummary = (run: IAiRun): string => {
    const created = run.output?.created_ids?.length || 0;
    const skipped = run.output?.skipped?.length || 0;
    return `${created} angelegt, ${skipped} übersprungen`;
};

export const ModalInventoryImport = ({ show, onClose, onFinished }: IModalInventoryImport) => {
    const dispatch = useDispatch();
    const [file, setFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [run, setRun] = useState<IAiRun | null>(null);
    const [busy, setBusy] = useState(false);
    const session = useRef(0);

    useEffect(() => {
        session.current += 1;
        setFile(null);
        setFileError(null);
        setRun(null);
        setBusy(false);
    }, [show]);

    useEffect(() => {
        if (!show || !run || isAiRunFinished(run)) {
            return;
        }
        const current = session.current;
        const timer = setInterval(async () => {
            try {
                const next = await getAiRun(run.id);
                if (current === session.current) {
                    setRun(next);
                }
            } catch (err) {
                if (current === session.current) {
                    clearInterval(timer);
                    toast.error(aiErrorMessage(err, "Status konnte nicht geladen werden"));
                }
            }
        }, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [show, run]);

    const handleFile = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const selected = ev.target.files && ev.target.files[0] ? ev.target.files[0] : null;
        setFile(selected);
        setFileError(importFileError(selected));
    };

    const start = async () => {
        const error = importFileError(file);
        setFileError(error);
        if (error || !file) {
            return;
        }
        setBusy(true);
        const current = session.current;
        try {
            const created = await createInventoryImport(file, IMPORT_INVENTORY_TYPE);
            if (current === session.current) {
                setRun(created);
            }
        } catch (err) {
            if (current === session.current) {
                toast.error(aiErrorMessage(err, "Import konnte nicht gestartet werden"));
            }
        } finally {
            if (current === session.current) {
                setBusy(false);
            }
        }
    };

    const finishWith = (message: string) => {
        toast.success(message);
        if (onFinished) {
            onFinished();
        }
        onClose();
    };

    const approveAll = async () => {
        if (!run) {
            return;
        }
        setBusy(true);
        try {
            const result = await approveAiRunItems(run.id);
            finishWith(`${result.approved} Positionen freigegeben`);
        } catch (err) {
            toast.error(aiErrorMessage(err, "Freigabe fehlgeschlagen"));
            setBusy(false);
        }
    };

    const discardAll = async () => {
        if (!run) {
            return;
        }
        if (!window.confirm("Alle nicht freigegebenen Positionen dieses Imports löschen?")) {
            return;
        }
        setBusy(true);
        try {
            const result = await discardAiRunItems(run.id);
            finishWith(`${result.discarded} Positionen verworfen`);
        } catch (err) {
            toast.error(aiErrorMessage(err, "Verwerfen fehlgeschlagen"));
            setBusy(false);
        }
    };

    const showUnapproved = () => {
        if (!run) {
            return;
        }
        dispatch(
            push(`/inventory_items/${IMPORT_INVENTORY_TYPE}?approved=false&ai_run_id=${run.id}`)
        );
        if (onFinished) {
            onFinished();
        }
        onClose();
    };

    const finished = isAiRunFinished(run);
    const skipped = run?.output?.skipped || [];
    const failedPages = run?.output?.failed_pages || [];

    return (
        <Modal show={show} onHide={onClose} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Artikelverzeichnis importieren</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!run && (
                    <>
                        <p className="text-muted">
                            Lieferanten-Artikelliste oder Angebot als PDF, JPG oder PNG hochladen.
                            Die KI liest Bezeichnung, Hersteller und Einkaufspreis aus und legt die
                            Positionen als nicht freigegebene Verkaufspositionen an.
                        </p>
                        <div className="form-group">
                            <label htmlFor="inventory-import-file">Datei</label>
                            <input
                                id="inventory-import-file"
                                type="file"
                                className="form-control-file"
                                accept=".pdf,image/png,image/jpeg"
                                onChange={handleFile}
                                disabled={busy}
                            />
                        </div>
                        {fileError && <Alert variant="danger">{fileError}</Alert>}
                    </>
                )}
                {run && !finished && (
                    <div className="text-center" style={{ padding: "40px 0" }}>
                        <Spinner animation="border" />
                        <p className="text-muted" style={{ marginTop: "12px" }}>
                            {progressLabel(run)}
                        </p>
                        <p className="text-muted">
                            Der Import läuft im Hintergrund und kann einige Minuten dauern. Sie
                            können das Fenster schließen – Sie erhalten eine E-Mail, sobald er
                            abgeschlossen ist.
                        </p>
                    </div>
                )}
                {run && finished && (
                    <>
                        {run.status === "failed" && (
                            <Alert variant="danger">{run.error || "Import fehlgeschlagen"}</Alert>
                        )}
                        {run.status === "succeeded" && (
                            <Alert variant="success">{resultSummary(run)}</Alert>
                        )}
                        {failedPages.length > 0 && run.status === "succeeded" && (
                            <Alert variant="warning">
                                {failedPages.map((page) => (
                                    <div key={page.page}>
                                        Seite {page.page} konnte nicht gelesen werden
                                    </div>
                                ))}
                            </Alert>
                        )}
                        {skipped.length > 0 && (
                            <details>
                                <summary>Übersprungen ({skipped.length})</summary>
                                <ul className="mt-2">
                                    {skipped.slice(0, MAX_SKIPPED_SHOWN).map((entry, index) => (
                                        <li key={index}>
                                            {entry.nr ? `${entry.nr} ` : ""}
                                            {entry.short_name || ""} – {entry.reason}
                                        </li>
                                    ))}
                                    {skipped.length > MAX_SKIPPED_SHOWN && (
                                        <li>… und {skipped.length - MAX_SKIPPED_SHOWN} weitere</li>
                                    )}
                                </ul>
                            </details>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                    disabled={busy}
                >
                    Schließen
                </button>
                {!run && (
                    <PrimaryButton
                        type="button"
                        onClick={start}
                        disabled={busy || !file || !!fileError}
                    >
                        {busy ? "Wird hochgeladen…" : "Import starten"}
                    </PrimaryButton>
                )}
                {run && finished && run.items_unapproved > 0 && (
                    <>
                        <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={discardAll}
                            disabled={busy}
                        >
                            Alle verwerfen
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={showUnapproved}
                            disabled={busy}
                        >
                            Nicht freigegebene anzeigen
                        </button>
                        <PrimaryButton type="button" onClick={approveAll} disabled={busy}>
                            Alle freigeben
                        </PrimaryButton>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default ModalInventoryImport;
